import { eventsApi } from '@/features/events/api';
import { stockApi, allocationsApi } from '@/features/stock/api';
import { xurveyApi, xurveyPublicUrl, type XurveySurvey } from '@/lib/api/xurvey';
import type { XkEvent, XkStation } from '@/lib/api/types';

/**
 * Nombre de la encuesta que se despliega por defecto. Vive aquí y no en quien
 * la usa porque la comparten el alta de evento y el quick start de la home.
 */
export const DEFAULT_TEMPLATE_NAME = 'Xotski Default Wheel';

/**
 * La encuesta que se pone en marcha por defecto.
 *
 * Ya no es una plantilla de Xotski: es una encuesta **publicada en Xurvey** con
 * destino Xotski y ámbito estación. Xotski no las diseña, las despliega
 * (protocolo de integración §2).
 *
 * Se prefiere la que lleva el nombre de siempre y, si no está, la primera que
 * haya. `null` significa que la empresa no tiene ninguna publicada, y eso lo
 * cuenta quien llama en vez de tragárselo: es un aviso accionable —publicar una
 * en Xurvey— y no un fallo.
 */
export async function resolveStartingPoint(): Promise<XurveySurvey | null> {
  const { items } = await xurveyApi.listSurveys({ scope: 'station' });
  const named = items.find(
    (s) => (s.name ?? '').trim().toLowerCase() === DEFAULT_TEMPLATE_NAME.toLowerCase(),
  );
  return named ?? items[0] ?? null;
}

export interface GameLink {
  surveyId: number;
  stationId: number;
  /** Absolute URL a guest can open on their phone. */
  url: string;
}

export function gameLinkUrl(surveyId: number, stationId: number): string {
  return `${window.location.origin}/s/${surveyId}/${stationId}`;
}

/**
 * Turn an event into a working game link, filling in whatever is missing
 * (task 8.2).
 *
 * Un enlace necesita tres cosas que el alta de evento no garantiza: una
 * estación que escanear, premios que repartir y la encuesta desplegada contra
 * esa estación. Quien empieza tenía que descubrirlas por su cuenta antes de
 * poder compartir nada, y todas son la opción que habría elegido igualmente.
 *
 * Idempotente: desplegar dos veces la misma estación devuelve el despliegue que
 * ya había, así que un evento ya montado sólo recupera su enlace.
 */
export async function ensureGameLink(
  event: Pick<XkEvent, 'id' | 'name'>,
  brandId: number,
): Promise<GameLink | null> {
  // 1 · A station to scan at.
  const existing = await eventsApi.listStations({ eventId: event.id });
  let station: XkStation | undefined = existing.items[0];
  if (!station) {
    station = await eventsApi.createStation({
      name: `${event.name} · 1`,
      eventId: event.id,
      companyId: brandId,
    });
  }

  // 2 · Algo que repartir.
  //
  // Una estación sin premios asignados pinta «Spin to Win!» sobre un juego
  // vacío: el enlace resuelve, la encuesta se pinta, y la promesa se rompe en
  // la última pantalla. Repartir el stock de la marca es lo que cualquiera
  // habría hecho a mano.
  await stockAllocationsFor(event.id, station.id, brandId);

  // 3 · El despliegue, que es lo que hace que el enlace exista.
  //
  // Antes esto clonaba una plantilla y estampaba su id en la estación. Ahora se
  // despliega la encuesta contra ESTA estación y el enlace sale del despliegue:
  // es lo que ata las respuestas a la estación de la que vinieron.
  const encuesta = await resolveStartingPoint();
  if (!encuesta) return null; // quien llama lo cuenta

  const despliegue = await xurveyApi.createDeployment(encuesta.id, {
    entityId: String(station.id),
    name: station.name,
  });

  // `surveysId` es la columna de Xotski que dice qué encuesta corre esta
  // estación, y no es legado: de ella viven la tarjeta del QR, el estado del
  // quick start y la cabecera de la estación. El despliegue no la escribe
  // —Xurvey no toca tablas de otras apps—, así que la anota Xotski.
  if (station.surveysId !== encuesta.id) {
    await eventsApi.updateStation(station.id, { surveysId: encuesta.id });
  }

  return { surveyId: encuesta.id, stationId: station.id, url: xurveyPublicUrl(despliegue.slug) };
}

/**
 * Give a station something to hand out: every xotski the brand owns, split so
 * no single prize can drain the catalogue.
 *
 * Idempotent — a station that already has allocations is left alone, because
 * after the first run the manager's own numbers are the truth and this should
 * never overwrite them.
 */
async function stockAllocationsFor(
  eventId: number,
  stationId: number,
  brandId: number,
): Promise<void> {
  const existing = await allocationsApi
    .list({ stationId })
    .catch(() => ({ items: [] as unknown[] }));
  if (existing.items.length > 0) return;

  const catalogue = await stockApi.list({ companyId: brandId }).catch(() => null);
  const items = catalogue?.items ?? [];
  if (items.length === 0) return;

  for (const [index, stock] of items.entries()) {
    // A conservative slice of what the brand actually holds. Enough for the
    // wheel to be playable, small enough that a first test event cannot spend
    // the entire inventory.
    const owned = stock.quantityRemaining ?? stock.initialQuantity ?? 0;
    const quantity = Math.max(1, Math.min(25, Math.floor(owned / 2) || 1));
    await allocationsApi
      .create({
        eventId,
        stationId,
        stockId: stock.id,
        quantity,
        available: quantity,
        position: index,
      })
      .catch(() => {
        // One prize failing to allocate should not cost the manager the link.
      });
  }
}
